import type { Locale, StageKind } from '@monrdv/shared';
import { localized } from '@monrdv/shared';
import type { DuctStage } from './DuctRun.js';

/**
 * Turning a tenant's pipeline stages into something drawable.
 *
 * Lives beside `DuctRun` because every screen that shows a stage needs exactly this, and
 * having each screen do its own mapping is how "awaiting payment" ends up amber on one
 * screen and orange on the next.
 */

/**
 * Stage colour comes from the stage's **kind**, never from the per-tenant `color` column.
 *
 * The three tenants each configured their own unrelated palette over the years, so honouring
 * those values would mean "en attente de paiement" is amber in one business and green in
 * another — and a coordinator who works across all three would have no colour vocabulary at
 * all. Mapping through `kind` gives one consistent meaning per colour everywhere, and keeps
 * every value a design token rather than a stored hex.
 *
 * The stored colour is still exported by the API; it is simply not what we draw with.
 */
export const STAGE_COLOR: Record<StageKind, string> = {
  intake: 'var(--stage-intake)',
  quoting: 'var(--stage-quoting)',
  scheduled: 'var(--stage-scheduled)',
  in_progress: 'var(--stage-in-progress)',
  awaiting_payment: 'var(--stage-awaiting-payment)',
  won: 'var(--stage-won)',
  lost: 'var(--stage-lost)',
  cancelled: 'var(--stage-cancelled)',
};

/** A stage as the API returns it. */
export type StageRow = {
  id: string;
  name: Record<string, string>;
  kind: StageKind;
  position: number;
  /** The tenant's stored colour. Deliberately unused — see STAGE_COLOR above. */
  color?: string;
  onBoard?: boolean;
};

/**
 * Map API stages onto duct-run stages, resolving the name into one locale and the colour
 * through `kind`. Order is by `position`, because the run has to read left to right in the
 * order the business actually works.
 */
export function toDuctStages(rows: StageRow[], locale: Locale): DuctStage[] {
  return [...rows]
    .sort((a, b) => a.position - b.position)
    .map((stage) => ({
      id: stage.id,
      name: localized(stage.name, locale),
      kind: stage.kind,
      color: STAGE_COLOR[stage.kind],
    }));
}

/** Whether work at this stage still needs somebody. Drives the default "open work" filters. */
export function isOpenStage(kind: StageKind): boolean {
  return kind !== 'won' && kind !== 'lost' && kind !== 'cancelled';
}
